import { Logger } from '_common/services';
import { ReduxInterface } from 'Editor/services';
import { EditorSelectionUtils, JsonRange } from '../_Common/Selection';

class SpellCheck {
  private editorContext: Editor.Context;
  constructor(editorContext: Editor.Context) {
    this.editorContext = editorContext;
  }

  destroy() {}

  /**
   * find next error ocurrence
   */
  findError(forward = true, fromStart = false): Promise<Editor.SpellCheck.Occurrence> {
    return new Promise(async (resolve, reject) => {
      let data = null;
      if (this.editorContext?.navigationManager?.isMarkerRendered(undefined, false)) {
        this.editorContext.visualizerManager?.selection.stopSelectionTracker();
        try {
          const editorSelection = this.editorContext?.DataManager?.selection.current[0];
          if (editorSelection) {
            if (forward) {
              data = await this.editorContext?.DataManager?.spellcheck.nextSpellcheckHit(
                null,
                editorSelection.end,
                fromStart,
              );

              // To cycle to the start of the document if no next occurrence is found
              if (!data) {
                const nodes = this.editorContext?.DataManager?.structure.getDocumentNodes();

                if (nodes?.length) {
                  const nextPosition: Editor.Selection.Position = {
                    b: nodes[0],
                    p: ['childNodes', 0], // path to element
                  };
                  data = await this.editorContext?.DataManager?.spellcheck.nextSpellcheckHit(
                    null,
                    nextPosition,
                    fromStart,
                  );
                }
              }
            } else {
              data = await this.editorContext?.DataManager?.spellcheck.previousSpellcheckHit(
                null,
                editorSelection.start,
              );

              // To cycle to the end of the document if no previous occurrence is found
              if (!data) {
                const nodes = this.editorContext?.DataManager?.structure.getDocumentNodes();

                if (nodes?.length) {
                  const lastNodeModel = this.editorContext?.DataManager?.nodes.getNodeModelById(
                    nodes[nodes.length - 1],
                  );

                  const lastNodeData = lastNodeModel?.selectedData();

                  if (lastNodeData) {
                    const nextPosition: Editor.Selection.Position = {
                      b: nodes[nodes.length - 1],
                      p: ['childNodes', lastNodeData.childNodes?.length || 0], // path to element needs to be at the end of the last node
                    };

                    data = await this.editorContext?.DataManager?.spellcheck.previousSpellcheckHit(
                      null,
                      nextPosition,
                    );
                  }
                }
              }
            }

            if (data) {
              const rangeData: Editor.Selection.RangeData = {
                end: data.location.end,
                start: data.location.start,
              };

              await this.editorContext?.visualizerManager?.centerAtBlock(rangeData.start.b);

              this.editorContext?.DataManager?.selection.setUserSelection(rangeData);

              data.rangeData = rangeData;
              data.blockId = rangeData.start.b;
            }
          }
        } catch (error) {
          Logger.captureException(error);
          reject(error);
        } finally {
          this.editorContext.visualizerManager?.selection.debounceStartSelectionTracker();
        }
      }
      resolve(data);
    });
  }
  async runSpellCheckFromStart() {
    const nodes = this.editorContext?.DataManager?.structure.getDocumentNodes();
    if (nodes) {
      await this.editorContext?.visualizerManager?.centerAtBlock(nodes[0]);

      const nextPosition: Editor.Selection.Position = {
        b: nodes[0],
        p: ['childNodes', 0],
      };
      const rangeData: Editor.Selection.RangeData = {
        end: nextPosition,
        start: nextPosition,
      };

      this.editorContext?.DataManager?.selection.setUserSelection(rangeData);
    }

    return this.findError(true, true);
  }

  restoreSelectionToError(
    rangeData: Editor.SpellCheck.Occurrence['rangeData'],
    wordToRestore: string,
  ) {
    if (this.editorContext?.navigationManager?.scrollIntoSelection([rangeData], false)) {
      const range = EditorSelectionUtils.getRange();
      if (range && range.toString() === wordToRestore) {
        return true;
      }

      EditorSelectionUtils.collapseToStart();
    }

    return false;
  }

  recheckError(rangeData: Editor.SpellCheck.Occurrence['rangeData'], wordToRestore: string) {
    if (this.restoreSelectionToError(rangeData, wordToRestore)) {
      return this.findError();
    }

    return Promise.reject();
  }

  /**
   * replace marker selectiond by suggestion
   * @param {Editor.SpellCheck.Occurrence} occurence
   * @param {String} suggestionToReplace
   */
  async replaceWord(occurence: Editor.SpellCheck.Occurrence, suggestionToReplace: string) {
    this.editorContext.visualizerManager?.selection.stopSelectionTracker();
    try {
      await this.editorContext.DataManager?.spellcheck?.changeOccurence(
        occurence,
        suggestionToReplace,
        ReduxInterface.isEditorTrackingStateOn(),
      );
      const resultJsonRange = new JsonRange(occurence.location.start);

      await this.editorContext?.navigationManager?.renderDocumentAtId(resultJsonRange.start.b);

      const resultRange = resultJsonRange.serializeToDOMRange();
      EditorSelectionUtils.applyRangeToSelection(resultRange);
      this.editorContext.selectionManager?.modifySelection(
        EditorSelectionUtils.SELECTION_CHANGE_TYPE.expand,
        EditorSelectionUtils.SELECTION_DIRECTION.forward,
        EditorSelectionUtils.SELECTION_GRANULARITY.word,
      );
    } catch (error) {
      Logger.captureException(error);
      throw error;
    } finally {
      this.editorContext.visualizerManager?.selection.debounceStartSelectionTracker();
    }
  }

  endSpellcheckCycle() {
    this.editorContext.DataManager?.spellcheck.endSpellcheckCycle();
  }

  ignoreErrorOnce() {
    return this.findError();
  }

  async ignoreAllOccurrences(term: string, lang: string) {
    return this.editorContext.DataManager?.spellcheck.ignoreAllOccurences(term, lang);
  }

  async addWordToDictionary(term: string, lang: string) {
    return this.editorContext.DataManager?.spellcheck.addWordToDictionary(term, lang);
  }

  async changeAllOcurrences(wordToReplace: string, suggestionToReplace: string) {
    return this.editorContext.DataManager?.spellcheck.changeAllOccurences(
      wordToReplace,
      suggestionToReplace,
      ReduxInterface.isEditorTrackingStateOn(),
    );
  }
}

export default SpellCheck;
